import type Luminary from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';
import { trace } from '@opentelemetry/api';

import { IconRegistry } from './registries/IconRegistry';
import { MediaRegistry } from './registries/MediaRegistry';
import { MusicRegistry } from './registries/MusicRegistry';
import { VoiceoverRegistry } from './registries/VoiceoverRegistry';
import type { Tool } from './tools/Tool';
import type { CallableToolArgsMap } from './callable-tools';
import { sliceAtLatestCompaction } from './compaction';
import type { ConversationData } from './conversationData';
import {
    ContextLengthExceededError,
    LLMClient,
    shouldUseResponsesApi,
    StreamInterruptedError,
} from './LLMClient';
import { StreamingUpdateScheduler } from './streamingUpdateScheduler';
import { dropOrphanedToolMessages } from './orphanedToolMessages';
import { truncateMessagesToFitContext } from './tokenEstimation';
import { reportIfVideoStateOversized } from './videoStateSizeGuard';
import { ToolExecutor } from './ToolExecutor';
import type {
    CodeInterpreterOutput,
    ConversationMessage,
    ImageContent,
    MessageContent,
    ReasoningBlock,
    TextContent,
    ToolCall,
    ToolDefinition,
    ToolMessage,
    ToolResult,
    Usage,
} from './types';
import {
    addUsageInto,
    emptyUsage,
    getContextLimit,
    isAssistantMessage,
    isSystemMessage,
    isToolMessage,
    isUserMessage,
    MessageRole,
} from './types';
import { VideoStateManager } from './video-state';

import { getApiKeyForEnvironment } from 'js/src/agent/ApiKeyManager';
import type { SceneAddressing } from 'js/src/agent/tools/LintVideoTool';
import {
    isConversationTool,
    sanitizeContentForTracing,
    sanitizeToolCallsForTracing,
} from 'js/src/agent/utils';
import { ApiService, ErrorEnum } from 'js/src/client';
import { formatVideoDataSnapshotMessage } from 'js/src/components/sketch/FileUpload';
import { describeTermination, formatTerminationMessage } from 'js/src/agent/terminationNotice';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import uuid from 'js/src/libs/uuid';
import { isApiErrorWithProperty } from 'js/src/types';

export enum ConversationStatus {
    IDLE = 'idle',
    LOADING = 'loading',
    STREAMING = 'streaming',
    ERROR = 'error',
}

// Messages produced when the user intentionally stops or clears the conversation.
// These propagate through several error-handling layers, so they are matched by
// substring wherever they end up.
const USER_CANCELLATION_MESSAGES = [
    'Conversation was cancelled by user',
    'Execution was cancelled',
    'Conversation was cleared',
];

function isUserCancellationMessage(message: string): boolean {
    return USER_CANCELLATION_MESSAGES.some((cancellation) => message.includes(cancellation));
}

export interface ConversationState {
    messages: ConversationMessage[];
    totalUsage: Usage;
    status: ConversationStatus;
    error: string | null;
    childConversations: Map<string, Conversation>;
    updateCounter: number; // Force re-renders when child conversations change
    toolCallConversations: Map<string, string[]>; // Maps tool call ID to child conversation IDs
    toolCallArtifacts: Map<string, string>; // Maps tool call ID to artifact task ID
    actualLatency?: number; // Actual wall-clock time in milliseconds from start to end of conversation
    turns?: number; // LLM round-trips (model responses) in this conversation — 1 for a single-shot turn
}

export interface ConversationConfig {
    model: string;
    systemMessage?: string;
    tools?: Tool[];
    maxIterations?: number;
    luminary?: Luminary;
    parentConversation?: Conversation;
    abortController?: AbortController;
    task: string;
    streaming?: boolean;
    videoId?: number;
    requiresVideoState: boolean;
    /**
     * Redux store accessor. Forwarded to the VideoStateManager so captured video state can include
     * per-scene transcript coverage (which needs Redux state to tell overlay from base scenes) and
     * transcript pauses (which live in the Lexical editor, not Luminary).
     */
    getState?: GetRootState;
    /**
     * How captured video state addresses scenes and scene groups (positional indices vs stable
     * scene_id/scene_group_id handles). Forwarded to the VideoStateManager. Defaults to 'index';
     * the Toolbox agent flips it to 'scene_id'.
     */
    sceneAddressing?: SceneAddressing;
    /**
     * Whether serialized colors carry a `(name)` alongside the hex. Forwarded to the
     * VideoStateManager. Defaults to true; the Toolbox turns it off since its model reads hex directly.
     */
    includeColorNames?: boolean;
    enableCodeInterpreter?: boolean;
    forceCodeInterpreter?: boolean;
    /** Opt in to Anthropic adaptive thinking + streamed reasoning summaries. */
    enableThinkingSummaries?: boolean;
    /**
     * Reasoning effort for this conversation's LLM calls (gpt-5.x: 'none' |
     * 'low' | 'medium' | 'high' | 'xhigh'; gemini-3.x thinking_level). For
     * gpt-5.x, anything above 'none' also routes through the Responses API.
     */
    reasoningEffort?: string;
    /**
     * Reads the shared save-conflict flag from redux (source of truth)
     * When true, autosaves are suppressed and only overwriteSave() proceeds.
     */
    isSaveConflict?: () => boolean;
    /**
     * Reports a detected save conflict up to redux.
     */
    reportConflict?: () => void;
}

/**
 * Enhanced Conversation class that manages LLM interactions, tool execution, and nested conversations.
 * Provides real-time notifications for all state changes including child conversation updates.
 */
/** Shown (under developer tools) where the model's context was compacted. */
export const COMPACTION_NOTICE =
    'Context compacted: from here on the model receives an encrypted summary in place of everything above.';

export class Conversation {
    private state: ConversationState;
    private listeners: Set<(state: ConversationState) => void> = new Set();
    private luminary?: Luminary;
    public toolExecutor?: ToolExecutor;
    private parentConversation?: Conversation;
    private conversationId: string;
    model: string;
    private abortController?: AbortController;
    private task: string;
    private streaming: boolean;
    private _videoId?: number;
    private enableCodeInterpreter: boolean;
    public forceCodeInterpreter: boolean;
    private enableThinkingSummaries: boolean;
    private reasoningEffort?: string;
    private compactionThreshold?: number;
    /** Set by a compaction; the next video-state injection explains itself as a refresh. */
    private stateRefreshAfterCompaction = false;
    public toolDefinitions?: ToolDefinition[];
    private conversationStartTime?: number;
    public readonly videoStateManager: VideoStateManager | undefined;
    private updateScheduler?: StreamingUpdateScheduler;

    private baseMessagesHash: string | null = null;
    private saveInFlight: Promise<void> | null = null;
    private queuedSave: { force: boolean } | null = null;
    private readonly isSaveConflict?: () => boolean;
    private readonly reportConflict?: () => void;

    constructor(config: ConversationConfig) {
        this.model = config.model;
        this.conversationId = this.generateId();
        this.luminary = config.luminary;
        this.parentConversation = config.parentConversation;
        this.abortController = config.abortController;
        this.task = config.task;
        this.streaming = config.streaming ?? false;
        this._videoId = config.videoId;
        this.enableCodeInterpreter = config.enableCodeInterpreter ?? false;
        this.forceCodeInterpreter = config.forceCodeInterpreter ?? false;
        this.enableThinkingSummaries = config.enableThinkingSummaries ?? false;
        this.reasoningEffort = config.reasoningEffort;
        this.isSaveConflict = config.isSaveConflict;
        this.reportConflict = config.reportConflict;

        this.state = {
            messages: config.systemMessage
                ? [
                      {
                          role: MessageRole.SYSTEM,
                          content: config.systemMessage,
                      },
                  ]
                : [],
            totalUsage: emptyUsage(),
            status: ConversationStatus.IDLE,
            error: null,
            childConversations: new Map(),
            updateCounter: 0,
            toolCallConversations: new Map(),
            toolCallArtifacts: new Map(),
            turns: 0,
        };

        if (config.requiresVideoState) {
            if (!config.luminary) {
                throw new Error('VideoStateManager requires access to luminary instance');
            }
            if (!config.getState) {
                // Redux state is required throughout the editing pipeline: coverage classifies
                // overlay vs base scenes, and pauses are read from the Lexical editor keyed off it.
                // Without it the video state would silently omit coverage and pauses — so fail loud
                // instead. (The only storeless capture path is the reference-video describer, which
                // bypasses VideoStateManager entirely.)
                throw new Error(
                    'VideoStateManager requires a getState accessor (Redux state is required for coverage and pauses)',
                );
            }
            this.videoStateManager = new VideoStateManager(
                config.luminary,
                config.getState,
                config.sceneAddressing ?? 'index',
                config.includeColorNames ?? true,
            );
        }

        // Initialize tool executor if tools provided
        if (config.tools && config.tools.length > 0) {
            this.toolExecutor = new ToolExecutor(config.tools, config.maxIterations);

            // attach tools to this conversation
            config.tools.forEach((tool) => {
                if (isConversationTool(tool)) {
                    tool.attachToConversation(this);
                }
            });
        }
    }

    /**
     * Generates a unique conversation ID
     */
    private generateId(): string {
        return uuid();
    }

    /**
     * Gets the conversation ID
     */
    public getId(): string {
        return this.conversationId;
    }

    /**
     * Whether the backend holds a row for this conversation. The id is generated
     * client-side at construction, so it is never itself evidence of a row —
     * only a save that came back with a hash (or a hydrate from stored data) is.
     * Callers that link out to /conversations/<id>/ must gate on this.
     */
    public isPersisted(): boolean {
        return this.baseMessagesHash !== null;
    }

    public get videoId(): number | undefined {
        return this._videoId;
    }

    /** Whether this conversation routes LLM calls through the OpenAI Responses API. */
    public usesResponsesApi(): boolean {
        return shouldUseResponsesApi(this.model, this.reasoningEffort);
    }

    /**
     * Gets the tools available to this conversation
     */
    public getTools(): Tool[] {
        return this.toolExecutor?.tools || [];
    }

    /**
     * Gets the task name for metrics tracking
     */
    public getTask(): string {
        return this.task;
    }

    /**
     * Gets the current state (deep copy to prevent mutations)
     */
    public getState(): ConversationState {
        return {
            ...this.state,
            messages: [...this.state.messages],
            totalUsage: { ...this.state.totalUsage },
            childConversations: new Map(this.state.childConversations),
            updateCounter: this.state.updateCounter,
        };
    }

    /**
     * Subscribes to state changes with real-time notifications
     */
    public subscribe(listener: (state: ConversationState) => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    // Streamed turns call notify() once per token, so it coalesces via the
    // scheduler; changes that must render immediately call flushNotify() instead.
    private notify(): void {
        this.getUpdateScheduler().schedule();
    }

    private flushNotify(): void {
        this.getUpdateScheduler().flushNow();
    }

    private getUpdateScheduler(): StreamingUpdateScheduler {
        if (!this.updateScheduler) {
            this.updateScheduler = new StreamingUpdateScheduler(() => this.notifyImmediate());
        }
        return this.updateScheduler;
    }

    // Do the real notification/bubbling; go through notify()/flushNotify() so the
    // coalescing invariants hold.
    private notifyImmediate(): void {
        const currentState = this.getState();

        this.listeners.forEach((listener) => listener(currentState));

        // Bubble up to parent conversation for nested real-time updates
        if (this.parentConversation) {
            this.parentConversation.onChildConversationUpdate(this.conversationId, currentState);
        }
    }

    /**
     * Handles child conversation updates and re-notifies listeners
     */
    private onChildConversationUpdate(childId: string, childState: ConversationState): void {
        // Increment update counter to force React re-renders
        this.state.updateCounter += 1;

        // Notify our listeners about the change
        this.notify();
    }

    /**
     * Adds a child conversation and sets up real-time updates
     */
    public addChildConversation(conversation: Conversation): void {
        this.state.childConversations.set(conversation.getId(), conversation);
        conversation.parentConversation = this;

        // Subscribe to child updates for real-time bubbling
        conversation.subscribe((childState) => {
            this.onChildConversationUpdate(conversation.getId(), childState);
        });

        this.notify();
    }

    /**
     * Associates a child conversation with a specific tool call ID.
     * This method handles both:
     * 1. Runtime-created conversations: adds to the map so they can be discovered
     * 2. Loaded conversations: map already populated, so this adds duplicates (but we check for that)
     */
    public addChildConversationForToolCall(conversation: Conversation, toolCallId: string): void {
        // Add the conversation as a regular child conversation
        this.addChildConversation(conversation);

        // Add to the toolCallConversations map (for runtime-created conversations)
        const existing = this.state.toolCallConversations.get(toolCallId) || [];
        const childId = conversation.getId();

        // Only add if not already present (avoids duplicates when loading from backend)
        if (!existing.includes(childId)) {
            existing.push(childId);
            this.state.toolCallConversations.set(toolCallId, existing);
        }

        this.notify();
    }

    /**
     * Gets child conversations for a specific tool call ID
     */
    public getChildConversationsForToolCall(toolCallId: string): Conversation[] {
        const conversationIds = this.state.toolCallConversations.get(toolCallId) || [];
        return conversationIds
            .map((id) => this.state.childConversations.get(id))
            .filter((conv): conv is Conversation => conv !== undefined);
    }

    /**
     * Associates an artifact task ID with a specific tool call ID.
     * Used for linking to oracle artifacts in admin.
     */
    public setArtifactTaskIdForToolCall(toolCallId: string, taskId: string): void {
        this.state.toolCallArtifacts.set(toolCallId, taskId);
    }

    public addMessage(message: ConversationMessage): void {
        this.state.messages.push(message);
        // Render immediately and flush any text buffered on the previous message.
        this.flushNotify();
    }

    public addSystemMessage(content: string, isContextMessage?: boolean): void {
        // A context message is the serialized video state injected on the getContextMessage path
        // (see RootAgent); flag it if it's large enough to crowd out the conversation on its own.
        if (isContextMessage) {
            reportIfVideoStateOversized(content, this.model, 'context_message', this.task);
        }
        this.addMessage({
            role: MessageRole.SYSTEM,
            content,
            ...(isContextMessage !== undefined && { isContextMessage }),
        });
    }

    async addUserMessage(content: MessageContent): Promise<void> {
        if (this.videoStateManager) {
            const stateDiff = await this.videoStateManager.getStateDiff();
            if (stateDiff.hasChanges) {
                reportIfVideoStateOversized(
                    stateDiff.description,
                    this.model,
                    'video_state_update',
                    this.task,
                );
                // Give the model the current video state as one hidden context message: a short
                // notice plus the state/diff, wrapped in <video-state-modifications>. Two things
                // keep it out of the user-facing UI — the isVideoStateUpdateMessage flag hides the
                // message, and the renderer strips whole <tag>…</tag> sections from user messages
                // (ConversationRenderer) — so the entire block must sit inside the tag.
                const notice = this.stateRefreshAfterCompaction
                    ? '[System: Context was compacted; the full current video state follows.]'
                    : this.getState().messages.length <= 1
                      ? '[System: Getting initial video state.]'
                      : '[External Edit: User modified the video directly. The updated video state is shown below.]';
                this.addMessage({
                    role: MessageRole.USER,
                    content: `<video-state-modifications>\n${notice}\n\n${stateDiff.description}\n</video-state-modifications>`,
                    isVideoStateUpdateMessage: true,
                });
                this.videoStateManager.setSnapshot(stateDiff.snapshot);
            }
            this.stateRefreshAfterCompaction = false;
        }
        this.addMessage({
            role: MessageRole.USER,
            content,
        });
    }

    public addAssistantMessage(
        content: MessageContent,
        toolCalls?: ToolCall[],
        usage?: Usage,
        codeInterpreterOutputs?: CodeInterpreterOutput[],
        openaiResponseId?: string,
        thinkingBlocks?: ReasoningBlock[],
        reasoningItems?: Array<Record<string, unknown>>,
        compactionItems?: Array<Record<string, unknown>>,
    ): string {
        const messageId = uuid();
        this.addMessage({
            id: messageId,
            role: MessageRole.ASSISTANT,
            content,
            tool_calls: toolCalls,
            usage,
            code_interpreter_outputs: codeInterpreterOutputs,
            openai_response_id: openaiResponseId,
            thinking_blocks: thinkingBlocks,
            reasoning_items: reasoningItems,
            compaction_items: compactionItems,
        });
        this.onCompaction(compactionItems);
        return messageId;
    }

    /**
     * Records why the run stopped, as a display-only message.
     *
     * Display-only keeps it out of the model's context while still persisting it with the
     * conversation, so the reason survives into the saved transcript and the render page.
     */
    public recordTermination(reason: string | null): void {
        if (!reason) {
            return;
        }

        this.addDisplayMessage([{ type: 'text', text: reason }]);
    }

    /**
     * Adds a display-only message that appears in the UI but is not sent to the model.
     * Used for debugging visualizations like before/after images in evaluations.
     */
    public addDisplayMessage(content: MessageContent): string {
        const messageId = uuid();
        this.addMessage({
            id: messageId,
            role: MessageRole.ASSISTANT,
            content,
            isDisplayOnly: true,
        });
        return messageId;
    }

    /**
     * Updates an assistant message content and immediately notifies.
     * Used for streaming responses to update content as it arrives.
     * @param content The new content for the message
     * @param toolCalls Optional tool calls to set on the message
     * @param usage Optional usage metrics
     * @param messageId ID of the specific message to update
     */
    public updateAssistantMessage(
        content: MessageContent,
        toolCalls?: ToolCall[],
        usage?: Usage,
        messageId?: string,
        thinkingBlocks?: ReasoningBlock[],
        reasoningItems?: Array<Record<string, unknown>>,
        compactionItems?: Array<Record<string, unknown>>,
    ): void {
        const message = this.state.messages.find(
            (msg) => isAssistantMessage(msg) && msg.id === messageId,
        );
        if (message && isAssistantMessage(message)) {
            message.content = content;
            if (toolCalls !== undefined) {
                message.tool_calls = toolCalls;
            }
            if (usage !== undefined) {
                message.usage = usage;
            }
            if (thinkingBlocks !== undefined) {
                message.thinking_blocks = thinkingBlocks;
            }
            if (reasoningItems !== undefined) {
                message.reasoning_items = reasoningItems;
            }
            if (compactionItems !== undefined) {
                message.compaction_items = compactionItems;
                this.onCompaction(compactionItems);
            }
            this.notify();
        }
    }

    /**
     * Runs after the server has compacted this conversation's context during a turn: fires the
     * analytics event (the counterpart of 'Agent Conversation Truncated'), adds a display-only
     * marker after the message carrying the compaction item (shown under developer tools and
     * persisted with the conversation), and resets the video-state snapshot so the next turn
     * re-sends the full state.
     */
    private onCompaction(compactionItems?: Array<Record<string, unknown>>): void {
        if (!compactionItems?.length) {
            return;
        }
        analytics.track('Agent Conversation Compacted', {
            model: this.model,
            task: this.task || 'unknown',
            message_count: this.state.messages.length,
            compaction_item_count: compactionItems.length,
        });
        this.addDisplayMessage(COMPACTION_NOTICE);
        // The model now holds only a summary of the video, so the next turn's state injection
        // must be the full state rather than a diff against a snapshot it can no longer see.
        this.videoStateManager?.clearSnapshot();
        this.stateRefreshAfterCompaction = true;
    }

    /**
     * Updates the live reasoning summary for an assistant message as the model
     * thinks. Display-only — the raw blocks for API replay are set separately
     * via updateAssistantMessage's thinkingBlocks argument.
     */
    public updateAssistantReasoning(reasoning: string, messageId?: string): void {
        const message = this.state.messages.find(
            (msg) => isAssistantMessage(msg) && msg.id === messageId,
        );
        if (message && isAssistantMessage(message)) {
            message.reasoning = reasoning;
            this.notify();
        }
    }

    /**
     * Adds a tool message and immediately notifies
     */
    public addToolMessage(
        content: ToolResult,
        toolCallId: string,
        isSuccess?: boolean,
        usage?: Usage,
        apiContent?: (TextContent | ImageContent)[],
    ): void {
        // Get child conversation IDs for this tool call
        const childConversationIds = this.state.toolCallConversations.get(toolCallId);
        // Get artifact task ID for this tool call (for oracle artifact links)
        const artifactTaskId = this.state.toolCallArtifacts.get(toolCallId);

        this.addMessage({
            role: MessageRole.TOOL,
            content,
            tool_call_id: toolCallId,
            apiContent,
            isSuccess,
            child_conversation_ids: childConversationIds,
            artifact_task_id: artifactTaskId,
            usage,
        });
    }

    /** Finds and updates the system message content (or adds it if missing) */
    public setSystemMessage(content: string): void {
        const systemMessageIndex = this.state.messages.findIndex((msg) => isSystemMessage(msg));
        if (systemMessageIndex !== -1) {
            this.state.messages[systemMessageIndex].content = content;
        } else {
            this.state.messages.unshift({
                role: MessageRole.SYSTEM,
                content,
            });
        }
        this.notify();
    }

    /** Updates the tools available to this conversation */
    public setTools(tools: Tool[]): void {
        if (tools && tools.length > 0) {
            const maxIterations = this.toolExecutor?.getMaxIterations() || 15;
            this.toolExecutor = new ToolExecutor(tools, maxIterations);
            tools.forEach((tool) => {
                if (isConversationTool(tool)) {
                    tool.attachToConversation(this);
                }
            });
        } else {
            this.toolExecutor = undefined;
        }
    }

    /**
     * Repoints video-state tracking (and luminary-derived saves) at a
     * different luminary instance. Used by Sketch when the selected video
     * draft changes.
     */
    public setVideoStateLuminary(luminary: Luminary): void {
        this.luminary = luminary;
        this.videoStateManager?.setLuminary(luminary);
    }

    /** Updates the model used for this conversation */
    public setModel(newModel: string): void {
        this.model = newModel;
    }

    /** Updates the reasoning effort override used for this conversation. Pass
     *  undefined to fall back to the model default. */
    public setReasoningEffort(effort: string | undefined): void {
        this.reasoningEffort = effort;
    }

    public getReasoningEffort(): string | undefined {
        return this.reasoningEffort;
    }

    /** Overrides the token count at which the Responses API compacts this conversation (a
     *  developer-tools knob for exercising compaction on short chats). Pass undefined to fall
     *  back to the model default. */
    public setCompactionThreshold(threshold: number | undefined): void {
        this.compactionThreshold = threshold;
    }

    public getCompactionThreshold(): number | undefined {
        return this.compactionThreshold;
    }

    /**
     * Sets conversation status and immediately notifies
     */
    public setStatus(status: ConversationStatus): void {
        this.state.status = status;
        // Terminal transitions (e.g. STREAMING -> IDLE) must render synchronously
        // and carry any still-buffered streamed text.
        this.flushNotify();
    }

    /**
     * Sets error message and status, then immediately notifies.
     * User-initiated cancellations are ignored — stopping the conversation is
     * intentional, so surfacing it as an error is redundant.
     */
    public setError(error: string | null): void {
        if (error && isUserCancellationMessage(error)) {
            return;
        }
        this.state.error = error;
        this.state.status = ConversationStatus.ERROR;
        this.flushNotify();
    }

    /**
     * Adds usage information and immediately notifies
     */
    public addUsage(usage: Usage): void {
        addUsageInto(this.state.totalUsage, usage);
        // Sum each LLM call's latency into the conversation total so evals can
        // report per-case model latency (the caller stamps usage.latency per call).
        if (usage.latency) {
            this.state.totalUsage.latency = (this.state.totalUsage.latency || 0) + usage.latency;
        }
        if (this.parentConversation) {
            this.parentConversation.addUsage(usage);
        }
        this.notify();
    }

    /**
     * Recomputes totalUsage from scratch after hydration. The live flow builds
     * totalUsage incrementally via addUsage (once per assistant completion, bubbled
     * up from children), but hydrate replaces messages wholesale, so the running
     * total has to be rederived. Sums the usage carried on this conversation's own
     * messages plus each hydrated child's totalUsage — matching what addUsage would
     * have accumulated live, so readers of the root's totalUsage see the same number
     * whether the conversation ran live or was hydrated.
     */
    private recomputeTotalUsage(): void {
        const total = emptyUsage();
        this.state.messages.forEach((message) => {
            if (isAssistantMessage(message) || isToolMessage(message)) {
                addUsageInto(total, message.usage);
            }
        });
        this.state.childConversations.forEach((child) =>
            addUsageInto(total, child.getState().totalUsage),
        );
        this.state.totalUsage = total;
    }

    /**
     * Cancels the current conversation execution and all child conversations
     */
    public cancel(): void {
        if (this.abortController) {
            this.abortController.abort(
                new DOMException('Conversation was cancelled by user', 'AbortError'),
            );
        }

        for (const childConversation of this.state.childConversations.values()) {
            childConversation.cancel();
        }
    }

    /**
     * Clears conversation while preserving system message
     */
    public clear(): void {
        const systemMessage = this.state.messages.find((m) => m.role === MessageRole.SYSTEM);
        this.state.messages = systemMessage ? [systemMessage] : [];
        this.state.totalUsage = emptyUsage();
        this.state.error = null;
        this.state.childConversations.clear();
        this.state.updateCounter = 0;
        this.state.turns = 0;
        this.abortController?.abort(new DOMException('Conversation was cleared', 'AbortError'));
        this.abortController = undefined;
        this.notify();
    }

    /**
     * Gets all messages
     */
    public getMessages(): ConversationMessage[] {
        return [...this.state.messages];
    }

    /**
     * Gets messages formatted for API calls
     */
    public getMessagesForAPI(): ConversationMessage[] {
        const lastContextMessageIndex = this.state.messages.findLastIndex(
            (msg) => msg.isContextMessage === true,
        );

        const mapped = this.state.messages
            .filter((msg, index) => {
                // Remove display-only messages (for debugging/visualization purposes)
                if (msg.isDisplayOnly === true) {
                    return false;
                }
                // Remove old context messages (except the most recent one)
                if (msg.isContextMessage === true && index !== lastContextMessageIndex) {
                    return false;
                }
                return true;
            })
            .map((msg): ConversationMessage => {
                if (isSystemMessage(msg)) {
                    return {
                        role: MessageRole.SYSTEM,
                        content: msg.content,
                    };
                }
                if (isUserMessage(msg)) {
                    return {
                        role: MessageRole.USER,
                        content: msg.content,
                    };
                }
                if (isAssistantMessage(msg)) {
                    return {
                        role: MessageRole.ASSISTANT,
                        content: msg.content,
                        ...(msg.tool_calls ? { tool_calls: msg.tool_calls } : {}),
                        ...(msg.thinking_blocks ? { thinking_blocks: msg.thinking_blocks } : {}),
                        ...(msg.reasoning_items ? { reasoning_items: msg.reasoning_items } : {}),
                        ...(msg.compaction_items ? { compaction_items: msg.compaction_items } : {}),
                        ...(msg.code_interpreter_outputs
                            ? { code_interpreter_outputs: msg.code_interpreter_outputs }
                            : {}),
                    };
                }
                // Must be a tool message since we've exhausted all other options in the union
                // When apiContent is set, substitute it for the LLM payload
                // (content stays as string for display/renderers).
                // Build as a plain object because apiContent may be multipart
                // (array of TextContent | ImageContent), which is wider than
                // ToolMessage's string-only content field.
                const toolContent: MessageContent = msg.apiContent || msg.content;
                const toolMsg: ToolMessage = {
                    role: MessageRole.TOOL,
                    content: toolContent as ToolResult,
                    tool_call_id: msg.tool_call_id,
                };
                if (msg.isSuccess !== undefined) {
                    toolMsg.isSuccess = msg.isSuccess;
                }
                return toolMsg;
            });

        // Only the Responses API can consume a compaction item; on any other path the full
        // history is still needed, so leave it to truncation.
        const windowed = dropOrphanedToolMessages(
            this.usesResponsesApi() ? sliceAtLatestCompaction(mapped).messages : mapped,
        );
        const toolDefinitions = this.toolExecutor?.getToolDefinitions();
        return this.truncateToFitContext(windowed, toolDefinitions);
    }

    /**
     * Truncates messages to fit within the model's context window.
     */
    private truncateToFitContext(
        messages: ConversationMessage[],
        tools?: ToolDefinition[],
    ): ConversationMessage[] {
        const contextLimit = getContextLimit(this.model);
        const result = truncateMessagesToFitContext(messages, contextLimit, tools);

        if (result.removedCount > 0) {
            analytics.track('Agent Conversation Truncated', {
                model: this.model,
                task: this.task || 'unknown',
                original_message_count: result.originalCount,
                removed_message_count: result.removedCount,
                remaining_message_count: result.messages.length,
                context_limit: contextLimit,
                estimated_tokens_before: result.estimatedTokensBefore,
                estimated_tokens_after: result.estimatedTokensAfter,
                still_over_limit: result.stillOverLimit,
            });
        }

        // Truncation couldn't fit the request: the non-removable messages exceed the limit on
        // their own. Report it so this surfaces instead of failing silently.
        if (result.stillOverLimit) {
            ErrorReporter.capture(new Error('Agent context still over limit after truncation'), {
                extra: {
                    model: this.model,
                    task: this.task || 'unknown',
                    context_limit: contextLimit,
                    estimated_tokens_after: result.estimatedTokensAfter,
                    remaining_message_count: result.messages.length,
                    removed_message_count: result.removedCount,
                },
            });
        }

        return result.messages;
    }

    /**
     * Internal method to actually do the LLM calls
     */
    private async processWithLLM({
        parentActionGroup,
        stopTool,
    }: {
        parentActionGroup?: string;
        stopTool?: string;
    }): Promise<{
        success: boolean;
        message?: string;
        error?: string;
        llmData?: {
            promptTokens: number;
            completionTokens: number;
            totalTokens: number;
            cacheReadInputTokens: number;
            cacheCreationInputTokens: number;
            response: string;
            hasToolCalls?: boolean;
            toolCallCount?: number;
            toolNames?: string;
            toolCalls?: string;
        };
    }> {
        const apiKey = getApiKeyForEnvironment();
        // Built per LLM call rather than once per turn so developer-tools overrides (reasoning
        // effort, compaction threshold) changed while a long tool loop runs apply to its next
        // iteration. The client is plain configuration; nothing carries over between calls.
        const createClient = () =>
            new LLMClient(apiKey, this.model, {
                enableCodeInterpreter: this.enableCodeInterpreter,
                forceCodeInterpreter: this.forceCodeInterpreter,
                enableThinkingSummaries: this.enableThinkingSummaries,
                reasoningEffort: this.reasoningEffort,
                compactionThreshold: this.compactionThreshold,
            });
        const client = createClient();
        const messages = this.getMessagesForAPI();
        const toolDefinitions = this.toolExecutor?.getToolDefinitions() || [];

        try {
            if (this.abortController?.signal.aborted) {
                return { success: false, error: 'Execution was cancelled' };
            }

            const startTime = performance.now();
            let response;
            if (this.streaming) {
                // Add empty assistant message first for streaming, track its ID
                const streamingMessageId = this.addAssistantMessage('');
                this.setStatus(ConversationStatus.STREAMING);

                response = await client.completeStreaming(
                    messages,
                    (content) => {
                        // Update the specific message by ID as it streams
                        this.updateAssistantMessage(
                            content,
                            undefined,
                            undefined,
                            streamingMessageId,
                        );
                    },
                    toolDefinitions,
                    this.abortController?.signal,
                    this.task,
                    (reasoning) => {
                        // Stream the reasoning summary so users see live progress
                        this.updateAssistantReasoning(reasoning, streamingMessageId);
                    },
                );

                const latency = performance.now() - startTime;

                // Update the specific message with final content and tool calls
                this.updateAssistantMessage(
                    response.content,
                    response.tool_calls,
                    {
                        ...response.usage,
                        latency,
                    },
                    streamingMessageId,
                    response.thinking_blocks,
                    response.reasoning_items,
                    response.compaction_items,
                );
                this.setStatus(ConversationStatus.IDLE);
            } else {
                let reasoningSummary = '';
                response = await client.complete(
                    messages,
                    toolDefinitions,
                    this.abortController?.signal,
                    this.task,
                    undefined,
                    (reasoning) => {
                        reasoningSummary = reasoning;
                    },
                );
                const latency = performance.now() - startTime;

                const messageId = this.addAssistantMessage(
                    response.content,
                    response.tool_calls,
                    { ...response.usage, latency },
                    response.code_interpreter_outputs,
                    response.openai_response_id,
                    response.thinking_blocks,
                    response.reasoning_items,
                    response.compaction_items,
                );
                if (reasoningSummary) {
                    this.updateAssistantReasoning(reasoningSummary, messageId);
                }
            }
            this.addUsage({ ...response.usage, latency: performance.now() - startTime });

            const hasToolCalls = response.tool_calls && response.tool_calls.length > 0;
            const llmData = {
                promptTokens: response.usage.input_tokens,
                completionTokens: response.usage.output_tokens,
                totalTokens: response.usage.total_tokens,
                cacheReadInputTokens: response.usage.cache_read_input_tokens || 0,
                cacheCreationInputTokens: response.usage.cache_creation_input_tokens || 0,
                response: hasToolCalls
                    ? response.content || '[empty - tool calls only]'
                    : sanitizeContentForTracing(response.content),
                hasToolCalls,
                ...(hasToolCalls && {
                    toolCallCount: response.tool_calls?.length || 0,
                    toolNames: response.tool_calls?.map((tc) => tc.function.name).join(',') || '',
                    toolCalls: sanitizeToolCallsForTracing(response.tool_calls!),
                }),
            };

            if (response.tool_calls && response.tool_calls.length > 0) {
                if (!this.toolExecutor) {
                    throw new Error('Tool executor is not configured for this conversation');
                }

                // Execute tools with iterative LLM calls
                const result = await this.toolExecutor.executeWithIterations(
                    response.tool_calls,
                    this,
                    {
                        parentActionGroup: parentActionGroup || 'default',
                        stopTool,
                        abortSignal: this.abortController?.signal,
                        abortController: this.abortController,
                        task: this.task,
                        videoStateManager: this.videoStateManager,
                        model: this.model,
                    },
                    async (messages, toolDefinitions) => {
                        if (this.abortController?.signal.aborted) {
                            throw new DOMException('Execution was cancelled', 'AbortError');
                        }

                        const iterationStartTime = performance.now();
                        const iterationClient = createClient();

                        if (this.streaming) {
                            // Add empty assistant message before streaming, track its ID
                            const iterationMessageId = this.addAssistantMessage('');
                            this.setStatus(ConversationStatus.STREAMING);

                            const streamResponse = await iterationClient.completeStreaming(
                                messages,
                                (content) => {
                                    // Update the specific message by ID as it streams
                                    this.updateAssistantMessage(
                                        content,
                                        undefined,
                                        undefined,
                                        iterationMessageId,
                                    );
                                },
                                toolDefinitions,
                                this.abortController?.signal,
                                this.task,
                                (reasoning) => {
                                    // Stream the reasoning summary for this tool iteration
                                    this.updateAssistantReasoning(reasoning, iterationMessageId);
                                },
                            );

                            const iterationLatency = performance.now() - iterationStartTime;

                            // Update the specific message with final content and tool calls
                            this.updateAssistantMessage(
                                streamResponse.content,
                                streamResponse.tool_calls,
                                {
                                    ...streamResponse.usage,
                                    latency: iterationLatency,
                                },
                                iterationMessageId,
                                streamResponse.thinking_blocks,
                                streamResponse.reasoning_items,
                                streamResponse.compaction_items,
                            );
                            this.setStatus(ConversationStatus.IDLE);

                            return {
                                ...streamResponse,
                                usage: { ...streamResponse.usage, latency: iterationLatency },
                            };
                        } else {
                            let reasoningSummary = '';
                            const response = await iterationClient.complete(
                                messages,
                                toolDefinitions,
                                this.abortController?.signal,
                                this.task,
                                undefined,
                                (reasoning) => {
                                    reasoningSummary = reasoning;
                                },
                            );
                            const iterationLatency = performance.now() - iterationStartTime;

                            const iterationMessageId = this.addAssistantMessage(
                                response.content,
                                response.tool_calls,
                                { ...response.usage, latency: iterationLatency },
                                response.code_interpreter_outputs,
                                response.openai_response_id,
                                response.thinking_blocks,
                                response.reasoning_items,
                                response.compaction_items,
                            );
                            if (reasoningSummary) {
                                this.updateAssistantReasoning(reasoningSummary, iterationMessageId);
                            }
                            return {
                                ...response,
                                usage: { ...response.usage, latency: iterationLatency },
                            };
                        }
                    },
                );

                if (result.success) {
                    return { success: true, message: result.result, llmData };
                } else {
                    return { success: false, error: result.result, llmData };
                }
            } else {
                return { success: true, message: response.content, llmData };
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            this.setError(errorMessage);

            // Don't report expected errors to Sentry:
            // - AbortErrors: user cancelled the operation
            // - Rate limit errors: tracked in Mixpanel, not a bug
            // - Stream interruption: transient network issue, user can retry
            const isAbortError = error instanceof Error && error.name === 'AbortError';
            const isRateLimitError =
                errorMessage.includes('Rate limit exceeded') ||
                errorMessage.includes('Daily usage limit reached');
            const isStreamInterruption = error instanceof StreamInterruptedError;
            const isContextLengthError = error instanceof ContextLengthExceededError;
            if (
                !isAbortError &&
                !isRateLimitError &&
                !isStreamInterruption &&
                !isContextLengthError
            ) {
                ErrorReporter.capture(error);
            }

            // Leave a breadcrumb in the transcript. Without it a run that stops here is
            // indistinguishable, after the fact, from one that simply ended.
            this.recordTermination(
                isStreamInterruption
                    ? formatTerminationMessage('stream interrupted', errorMessage)
                    : describeTermination(error),
            );

            return { success: false, error: errorMessage };
        }
    }

    /**
     * Executes a conversation with LLM including tool calls
     * This is the main method that handles the LLM ↔ tool execution loop
     */

    public async executeWithLLM({
        parentActionGroup,
        stopTool,
        addBeforeAfterVideoDataDisplayMessages,
        addVideoDataDisplayMessageAfterUserRequest,
    }: {
        parentActionGroup?: string;
        stopTool?: string;
        addBeforeAfterVideoDataDisplayMessages?: boolean;
        addVideoDataDisplayMessageAfterUserRequest?: boolean;
    }): Promise<{ success: boolean; message?: string; error?: string }> {
        this.abortController = new AbortController();
        this.setStatus(ConversationStatus.LOADING);

        // Track conversation start time
        if (!this.conversationStartTime) {
            this.conversationStartTime = Date.now();
        }

        const tracer = trace.getTracer('lumen5-agent');
        return tracer.startActiveSpan('conversation-message', async (span) => {
            const lastUserMessage = this.state.messages
                .slice()
                .reverse()
                .find((msg) => msg.role === MessageRole.USER);
            span.setAttributes({
                'agent.model': this.model,
                'agent.max_iterations': this.toolExecutor?.getMaxIterations() || 0,
                'message.type': 'user',
                'message.content': (lastUserMessage?.content as string | undefined) || '',
            });

            if (this.abortController?.signal.aborted) {
                span.setStatus({ code: 2, message: 'Execution was cancelled' });
                this.setStatus(ConversationStatus.IDLE);
                this.recordTermination(
                    formatTerminationMessage('cancelled by user', 'Execution was cancelled'),
                );
                return { success: false, error: 'Execution was cancelled' };
            }

            const result = await this.processWithLLM({
                parentActionGroup,
                stopTool,
            });

            if (!result.success) {
                span.recordException(new Error(result.error || 'Processing failed'));
                span.setStatus({ code: 2, message: result.error || 'Processing failed' });
                this.setError(result.error || 'Processing failed');
            } else {
                span.setStatus({ code: 1 });
            }

            // Add LLM data to span if available
            if (result.llmData) {
                span.setAttributes({
                    'llm.input_tokens': result.llmData.promptTokens,
                    'llm.output_tokens': result.llmData.completionTokens,
                    'llm.total_tokens': result.llmData.totalTokens,
                    'llm.cache_read_input_tokens': result.llmData.cacheReadInputTokens || 0,
                    'llm.cache_creation_input_tokens': result.llmData.cacheCreationInputTokens || 0,
                    'llm.response': result.llmData.response,
                    'llm.has_tool_calls': result.llmData.hasToolCalls || false,
                });

                if (result.llmData.toolCalls) {
                    span.setAttributes({
                        'llm.tool_call_count': result.llmData.toolCallCount || 0,
                        'llm.tool_names': result.llmData.toolNames || '',
                        'llm.tool_calls': result.llmData.toolCalls,
                    });
                }
            }

            this.setStatus(ConversationStatus.IDLE);

            // Calculate and store actual latency
            if (this.conversationStartTime) {
                const actualLatency = Date.now() - this.conversationStartTime;
                this.state.actualLatency = actualLatency;
            }

            // Turns = LLM round-trips for this conversation: one usage-bearing
            // assistant message per model response (the initial call plus each
            // tool-loop iteration). Counts this conversation only, not child
            // sub-agents. Meaningful even when the tool loop never runs (routing
            // evals with maxIterations 0 → 1), unlike ToolExecutor.iterationsUsed.
            this.state.turns = this.state.messages.filter(
                (message) => isAssistantMessage(message) && message.usage != null,
            ).length;

            span.end();

            // Only add AFTER snapshot if the LLM call succeeded
            // This prevents misleading before/after pairs when the LLM fails
            //
            // executeWithLLM runs once per completed user request (the whole tool
            // loop happens inside processWithLLM), so this block is also where we
            // capture a single "view video" snapshot per request for agents that
            // opt in via addVideoDataDisplayMessageAfterUserRequest — without the
            // noisy before snapshot of the before/after mode.
            if (
                (addBeforeAfterVideoDataDisplayMessages ||
                    addVideoDataDisplayMessageAfterUserRequest) &&
                result.success
            ) {
                // Returns immediately; the upload and the save that persists the
                // link happen in the background (see addVideoDataDisplayMessages).
                this.addVideoDataDisplayMessages();
            }

            // Save conversation after completing LLM interaction
            void this.saveConversation();

            return result;
        });
    }

    /**
     * Executes a tool directly without involving the LLM.
     * Produces conversation messages so tool call is part of the conversation history.
     */
    public async executeTool<T extends keyof CallableToolArgsMap>(
        toolName: T,
        params: CallableToolArgsMap[T],
        assistantMessage?: string,
    ): Promise<{ success: boolean; error?: string }> {
        try {
            if (!this.toolExecutor) {
                throw new Error('Tool executor is not configured for this conversation');
            }

            if (assistantMessage) {
                this.addAssistantMessage(assistantMessage);
            }

            const toolCallId = uuid();
            const toolCall: ToolCall = {
                id: toolCallId,
                type: 'function',
                function: {
                    name: toolName,
                    arguments: JSON.stringify(params),
                },
            };

            this.addAssistantMessage('', [toolCall]);

            await this.toolExecutor.executeTools([toolCall], this, {
                parentActionGroup: uuid(),
                task: this.task,
                videoStateManager: this.videoStateManager,
                model: this.model,
            });
            void this.saveConversation();

            return { success: true };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            this.setError(errorMessage);
            ErrorReporter.capture(error);
            return { success: false, error: errorMessage };
        }
    }

    /**
     * Gets parent conversation
     */
    public getParent(): Conversation | undefined {
        return this.parentConversation;
    }

    /**
     * Gets the depth of this conversation in the hierarchy
     */
    public getDepth(): number {
        let depth = 0;
        let current: Conversation | undefined = this.parentConversation;
        while (current) {
            depth++;
            current = current.parentConversation;
        }
        return depth;
    }

    /**
     * Saves the conversation to the backend API
     * Should be called after significant conversation updates (e.g., after LLM responses)
     */
    public async saveConversation(options?: { videoId?: number }): Promise<void> {
        // A toolbox chat is created before its video exists; the turn-end handoff
        // links the video by saving with its id. Write-once (see linkVideo).
        if (options?.videoId != null) {
            this.linkVideo(options.videoId);
        }

        // Once conflicted, conversation cannot save until the user resolves the conflict
        // via overwriteSave() or does a full page reload.
        if (this.isSaveConflict?.()) {
            return;
        }

        await this.enqueueSave(false);
    }

    /**
     * Force-saves the conversation, overwriting whatever another session stored.
     */
    public async overwriteSave(): Promise<void> {
        await this.enqueueSave(true);
    }

    /**
     * Runs at most one save at a time. If called while a save is in flight,
     * coalesces into a single trailing save that runs once the current one finishes.
     */
    private enqueueSave(force: boolean): Promise<void> {
        if (this.saveInFlight) {
            this.queuedSave = { force: (this.queuedSave?.force ?? false) || force };
            return this.saveInFlight;
        }
        this.saveInFlight = this.drainSaves(force).finally(() => {
            this.saveInFlight = null;
        });
        return this.saveInFlight;
    }

    private async drainSaves(force: boolean): Promise<void> {
        await this.performSave(force);
        while (this.queuedSave) {
            const next = this.queuedSave;
            this.queuedSave = null;
            // A conflict detected by a prior pass suppresses further non-force
            // trailing saves — only an explicit overwriteSave proceeds.
            if (this.isSaveConflict?.() && !next.force) {
                break;
            }
            await this.performSave(next.force);
        }
    }

    private async performSave(forceSave: boolean): Promise<void> {
        const data = this.save();

        try {
            const response = await ApiService.saveConversation({
                body: {
                    conversation_id: data.conversation_id,
                    messages: data.messages as unknown as Record<string, unknown>[],
                    model: data.model,
                    tools: data.tools,
                    video_id: data.video_id,
                    task_type: data.task_type,
                    actual_latency: data.actual_latency,
                    media_registry: data.media_registry,
                    music_registry: data.music_registry,
                    voiceover_registry: data.voiceover_registry,
                    icon_registry: data.icon_registry,
                    base_messages_hash: this.baseMessagesHash,
                    is_force_save: forceSave,
                },
            });
            // Adopt the refreshed baseline so the next (possibly coalesced) save
            // carries the correct hash.
            if (response.data?.messages_hash != null) {
                const wasPersisted = this.isPersisted();
                this.baseMessagesHash = response.data.messages_hash;
                // Becoming persisted is what makes /conversations/<id>/ resolvable,
                // so views offering that link need a render when it first happens.
                if (!wasPersisted) {
                    this.flushNotify();
                }
            }
        } catch (error) {
            if (
                isApiErrorWithProperty(error, 'error_code') &&
                error.body.error_code === ErrorEnum.OVERWRITING_DATA
            ) {
                // Report the conflict up to the shared source of truth rather than
                // rethrowing; the shared modal takes over from here.
                this.reportConflict?.();
                return;
            }
            // All other errors are non-critical: swallow and report.
            ErrorReporter.capture(error);
        }
    }

    /**
     * Snapshot the current video and add a "View video state" link for it.
     *
     * Returns as soon as the message has a place in the transcript, not when the
     * upload finishes: serializing the whole video and pushing it to GCS takes
     * seconds, and the Toolbox is the production editor chat's default agent, so
     * anything awaiting this would sit on the turn's critical path. Callers that
     * need the link in a particular position get it — the slot is reserved
     * synchronously and filled in when the upload lands.
     *
     * @param reserveSlot reserve the message's position now, for a "before"
     * snapshot that must sit ahead of messages added later in the same turn. An
     * "after" snapshot appends whenever it resolves, so it can leave this off.
     */
    public addVideoDataDisplayMessages({ reserveSlot = false } = {}): void {
        if (!this.luminary) {
            return;
        }

        // A blank/unpopulated luminary (e.g. the user only chatted and never
        // had a video built) has no scenes — there's nothing to view, so skip
        // both the snapshot upload and the "View video state" link.
        if (!this.luminary.scenes?.length) {
            return;
        }

        // Read the video synchronously: an upload started a tick later would
        // capture whatever the turn has since done to it, not the starting state.
        const videoData = this.luminary.save();

        // In before/after mode a turn's "after" and the next turn's "before" describe
        // the same untouched video, so without this every turn past the first would
        // upload a byte-identical snapshot and show a second link to it.
        const fingerprint = JSON.stringify(videoData);
        if (fingerprint === this.lastVideoSnapshotFingerprint) {
            return;
        }
        this.lastVideoSnapshotFingerprint = fingerprint;

        const placeholderId = reserveSlot ? this.addDisplayMessage([]) : undefined;

        const upload = (async () => {
            try {
                const response = await ApiService.saveVideoDataSnapshotCreate({
                    body: {
                        video_data: videoData,
                        conversation_id: this.conversationId,
                    },
                });

                const url = response.data?.url;
                if (!url) {
                    return;
                }
                const content: MessageContent = [
                    {
                        type: 'text',
                        text: formatVideoDataSnapshotMessage(this.conversationId, url),
                    },
                ];
                if (placeholderId) {
                    this.updateAssistantMessage(content, undefined, undefined, placeholderId);
                } else {
                    this.addDisplayMessage(content);
                }
            } catch (error) {
                ErrorReporter.capture(error, { extra: { context: 'save_video_data_snapshot' } });
                return;
            }
            // The link resolves after the turn's own save, so without this it never
            // reaches the server and is lost on reload. Kept out of the catch above so
            // a failed save isn't reported as a failed snapshot, and swallowed on its
            // own terms: saveConversation has its own error reporting.
            try {
                await this.saveConversation();
            } catch {
                // Already reported by saveConversation.
            }
        })();
        this.pendingVideoSnapshotUploads.add(upload);
        void upload.finally(() => this.pendingVideoSnapshotUploads.delete(upload));
    }

    /**
     * Resolves once every snapshot upload started so far has appended its message. Prod never awaits
     * this (uploads stay off the turn's critical path); an eval that serializes the trace the instant
     * the turn returns awaits it so the "after" link is present. Resolves immediately when nothing is
     * in flight — an empty video or an unchanged one never uploads (see the guards above), so this
     * never blocks a turn that had no snapshot to take.
     */
    public async waitForPendingVideoSnapshots(): Promise<void> {
        await Promise.allSettled([...this.pendingVideoSnapshotUploads]);
    }

    /** In-flight snapshot uploads, so a caller can await the "after" link before serializing. */
    private pendingVideoSnapshotUploads = new Set<Promise<void>>();

    /** The last video we snapshotted, so an unchanged one isn't uploaded twice. */
    private lastVideoSnapshotFingerprint?: string;

    /** Write-once video link: set it the first time, but never relink to a different video. */
    private linkVideo(videoId: number): void {
        if (this._videoId === videoId) {
            return;
        }
        if (this._videoId != null) {
            ErrorReporter.capture(
                new Error('Attempted to relink a conversation to a different video'),
                {
                    extra: {
                        conversationId: this.conversationId,
                        from: this._videoId,
                        to: videoId,
                    },
                },
            );
            return;
        }
        this._videoId = videoId;
    }

    /**
     * Serialize the conversation to a JSON-compatible object.
     * Returns the data structure that can be sent to the backend API.
     */
    public save() {
        const toolDefinitions = this.toolExecutor?.getToolDefinitions();

        return {
            conversation_id: this.conversationId,
            messages: this.state.messages,
            model: this.model,
            tools: toolDefinitions,
            video_id: this._videoId,
            task_type: this.task,
            actual_latency: this.state.actualLatency,
            media_registry: MediaRegistry.getInstance().serialize(),
            music_registry: MusicRegistry.getInstance().serialize(),
            voiceover_registry: VoiceoverRegistry.getInstance().serialize(),
            icon_registry: IconRegistry.getInstance().serialize(),
        };
    }

    /**
     * Build a display-only Conversation from saved data: a fresh (tool-less, no
     * video state) instance hydrated with the saved messages, registries, and child
     * subtree. Accepts the recursive `ConversationData` snapshot from
     * `fetchConversationData` (and, single-node, the shape returned by save()).
     * `isRoot` is false only for the recursive child rebuild inside hydrate.
     */
    public static fromData(data: ConversationData, isRoot = true): Conversation {
        const conversation = new Conversation({
            model: data.model,
            videoId: data.video_id ?? undefined,
            task: data.task_type ?? 'unknown',
            requiresVideoState: false,
        });

        // Display-only tool definitions are for rendering and stay out of hydrate,
        // which is tool-agnostic.
        if (data.tools) {
            conversation.toolDefinitions = data.tools;
        }

        conversation.hydrate(data, isRoot);

        // Set to idle status
        conversation.setStatus(ConversationStatus.IDLE);

        return conversation;
    }

    /**
     * Load a `ConversationData` (the source of truth) into this conversation: id,
     * videoId, messages, the toolCallConversations map, the child subtree, and (at the
     * root only) the registries. Replaces existing state, so it works on a fresh
     * instance (fromData) and a live one (resume) alike.
     */
    public hydrate(data: ConversationData, isRoot = true): void {
        this.conversationId = data.id;
        this.baseMessagesHash = data.messages_hash ?? null;

        if (data.video_id != null) {
            this._videoId = data.video_id;
        }

        this.state.messages = [...data.messages];
        this.state.toolCallConversations = new Map();
        this.state.childConversations = new Map();
        this.state.messages.forEach((message) => {
            if (isToolMessage(message) && message.child_conversation_ids) {
                this.state.toolCallConversations.set(
                    message.tool_call_id,
                    message.child_conversation_ids,
                );
            }
        });

        this.flushNotify();

        if (data.children) {
            const childDataById = new Map<string, ConversationData>();
            data.children.forEach((childData) => {
                childDataById.set(childData.id, childData);
            });

            this.state.toolCallConversations.forEach((childIds, toolCallId) => {
                childIds.forEach((childId) => {
                    // The loader omits children it can't fetch, so a referenced id may be absent.
                    const childData = childDataById.get(childId);
                    if (!childData) {
                        return;
                    }
                    const child = Conversation.fromData(childData, false);
                    this.addChildConversationForToolCall(child, toolCallId);
                });
            });
        }

        this.recomputeTotalUsage();

        // Registries are global singletons shared across the whole tree, and
        // loadFromSerialized replaces rather than merges. The root snapshot is the superset,
        // so load it once at the root instead of clobbering per node.
        if (isRoot) {
            if (data.media_registry) {
                MediaRegistry.getInstance().loadFromSerialized(data.media_registry);
            }
            if (data.music_registry) {
                MusicRegistry.getInstance().loadFromSerialized(data.music_registry);
            }
            if (data.voiceover_registry) {
                VoiceoverRegistry.getInstance().loadFromSerialized(data.voiceover_registry);
            }
            if (data.icon_registry) {
                IconRegistry.getInstance().loadFromSerialized(data.icon_registry);
            }
        }
    }
}
